/**
 * What a SAVED payment method can still change, per processor (PD-16214).
 *
 * Once a card is vaulted, which of its fields can be edited depends on the
 * gateway, not on our data model:
 *
 * - CyberSource, Authorize.Net and the mock partner accept a new expiration
 *   date on the stored token, so the edit form may offer it.
 * - Adyen and NMI keep the card data immutable. Adyen holds no mutable
 *   billing profile at all (cardholder name and billing address live only on
 *   our side), and NMI's Customer Vault update takes the billing profile but
 *   not the card. Editing the name and address is still a complete, correct
 *   operation for both; only the expiration date is off limits, because a
 *   value the processor never sees would silently disagree with the token it
 *   charges against, and could hide a card-expiring warning without the card
 *   actually being renewed.
 *
 * Keyed by `PaymentProcessorConfigEntity.processorType`. Anything not listed
 * is treated as not editable, so a new gateway has to opt in deliberately.
 */
const PROCESSORS_WITH_UPDATABLE_EXPIRATION: ReadonlySet<string> = new Set([
	'cybersource',
	'authorize_net',
	'mock',
]);

const PROCESSORS_SUPPORTING_EDIT: ReadonlySet<string> = new Set([
	...PROCESSORS_WITH_UPDATABLE_EXPIRATION,
	'adyen',
	'nmi',
]);

/**
 * Whether a saved payment method on this processor can be edited at all
 * (cardholder name and billing address always; expiration date only where
 * `supportsExpirationDateUpdate` allows).
 */
export const supportsPaymentMethodEdit = (processorType: string): boolean =>
	PROCESSORS_SUPPORTING_EDIT.has(processorType);

/**
 * Whether the processor will take a new expiration date onto the stored
 * token. Where it will not, the edit form shows the date for reference only.
 */
export const supportsExpirationDateUpdate = (processorType: string): boolean =>
	PROCESSORS_WITH_UPDATABLE_EXPIRATION.has(processorType);
